/**
 * First-touch acquisition attribution.
 *
 * The marketing site (site-mcp-showcase LandingPageInterceptor) writes an `mcps_lp` cookie on a
 * visitor's very first page view and never overwrites it. Format is
 * base64url(no padding) of `<epochMillis>|<path+query>`.
 *
 * In production the SPA is same-origin with the API, so the cookie reaches
 * UserManager.register on its own and this module is redundant. It exists for the dev setup,
 * where the CRA dev server (:5553) is cross-origin to the API (:5552) and fetch omits cookies.
 * The backend re-validates whatever we send, so a tampered cookie can only produce a null.
 */

const COOKIE_NAME = "mcps_lp";

const readCookie = (name) => {
  const prefix = name + "=";
  const parts = (document.cookie || "").split(";");
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    if (part.indexOf(prefix) === 0) {
      return part.substring(prefix.length);
    }
  }
  return null;
};

const decodeBase64Url = (value) => {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder("utf-8").decode(bytes);
};

/**
 * @returns {{landingPageUrl: string, landingPageDate: number}|{}} fields to merge into the
 *   register payload, or an empty object when there is nothing trustworthy to report.
 */
export const getLandingPageFields = () => {
  try {
    const raw = readCookie(COOKIE_NAME);
    if (!raw) {
      return {};
    }
    const decoded = decodeBase64Url(raw);
    const sep = decoded.indexOf("|");
    if (sep <= 0 || sep === decoded.length - 1) {
      return {};
    }
    const millis = parseInt(decoded.substring(0, sep), 10);
    const url = decoded.substring(sep + 1);
    if (!isFinite(millis) || url.charAt(0) !== "/") {
      return {};
    }
    return { landingPageUrl: url, landingPageDate: millis };
  } catch (e) {
    // Attribution must never block a registration.
    return {};
  }
};

export default getLandingPageFields;

/**
 * Free-tool run handed over by the marketing site for the user to claim after registering.
 *
 * The tool pages store the run uid under this key when a visitor clicks "Save this result"; the
 * site and the SPA are same-origin in production so localStorage carries it across. Consumed once
 * and cleared, so a failed claim never retries forever.
 */
const PENDING_CLAIM_KEY = "mcps_pending_claim";

export const takePendingToolRunUid = () => {
  try {
    const uid = window.localStorage.getItem(PENDING_CLAIM_KEY);
    if (uid) {
      window.localStorage.removeItem(PENDING_CLAIM_KEY);
    }
    return uid || null;
  } catch (e) {
    return null;
  }
};
